import { initializeFaro, getWebInstrumentations, MetaAttributes } from '@grafana/faro-react';
import { TracingInstrumentation } from '@grafana/faro-web-tracing';
import { FetchTransport } from '@grafana/faro-web-sdk';
import type { Faro } from '@grafana/faro-web-sdk';

export interface GrafanaConfig {
  collectorUrl: string;
  appName: string;
  environment: string;
  version?: string;
  // apiKey опционален для self-hosted решений с Grafana Agent
  // Для Grafana Cloud Frontend Observability не требуется
  apiKey?: string;
}

let faroInstance: Faro | undefined = undefined;

/**
 * Инициализация Grafana Faro для мониторинга фронтенда
 *
 * @param config - Конфигурация Faro
 * @returns Экземпляр Faro или undefined если инициализация не удалась
 */
export const initializeGrafanaFaro = (config: GrafanaConfig): Faro | undefined => {
  // Не инициализируем повторно
  if (faroInstance) {
    console.warn('[GrafanaFaro] Already initialized');
    return faroInstance;
  }

  // Пропускаем инициализацию если нет валидного URL
  if (!config.collectorUrl || config.collectorUrl === 'https://your-collector-url.faro.grafana.net/collect') {
    console.warn('[GrafanaFaro] Skipping initialization - no valid collector URL');
    return undefined;
  }

  try {
    const faroConfig = {
      app: {
        name: config.appName,
        version: config.version || import.meta.env.VITE_APP_VERSION || '1.0.0',
        environment: config.environment,
      },
      // Конфигурация транспорта для предотвращения "Task buffer full"
      // https://github.com/grafana/faro-web-sdk/issues/197
      transports: [
        new FetchTransport({
          url: config.collectorUrl,
          ...(config.apiKey ? { apiKey: config.apiKey } : {}),
          // Увеличен буфер для предотвращения переполнения при высокой нагрузке
          bufferSize: 100,
          // Количество параллельных запросов к коллектору
          concurrency: 5,
          // Время ожидания перед повторной отправкой при rate limiting (мс)
          defaultRateLimitBackoffMs: 1000,
        }),
      ],
      instrumentations: [
        ...getWebInstrumentations({
          // Отключаем captureConsole чтобы уменьшить количество событий
          // (консоль создает много шума в dev режиме)
          captureConsole: false,
        }),
        // Tracing для отслеживания fetch/XMLHttpRequest requests с distributed tracing
        new TracingInstrumentation({
          // Настройки propagation traceparent для CORS запросов
          instrumentationOptions: {
            propagateTraceHeaderCorsUrls: [
              // Production backend API
              /https:\/\/api\.voice2doc\.com/,
              // Supabase backend
              /https:\/\/sb\.voice2doc\.com/,
            ],
          },
        }),
      ],
      // Игнорируем запросы к Яндекс Метрике и другие URL
      ignoreUrls: [/mc\.yandex\.ru/],
      // Игнорируем распространенные некритичные ошибки
      ignoreErrors: [
        /^Script error\.?$/,
        /^ResizeObserver loop limit exceeded$/,
        /^Non-Error promise rejection captured/,
        /chrome-extension:\/\//,
        /moz-extension:\/\//,
        // Ошибки самого Faro транспорта чтобы избежать бесконечного цикла
        /Task buffer full/,
        /Failed sending payload/,
        /Failed to fetch/,
        /net::ERR_TIMED_OUT/,
      ],
      // Дополнительные метаданные
      meta: {
        // Можно добавить пользовательские атрибуты
      } as MetaAttributes,
    };

    faroInstance = initializeFaro(faroConfig);

    console.log('[GrafanaFaro] Initialized successfully');
    return faroInstance;
  } catch (error) {
    console.error('[GrafanaFaro] Initialization failed:', error);
    return undefined;
  }
};

/**
 * Получить экземпляр Faro
 */
export const getFaro = (): Faro | undefined => faroInstance;

/**
 * Проверить, инициализирован ли Faro
 */
export const isFaroInitialized = (): boolean => !!faroInstance;
